import { z } from 'zod';

export const MCP_SERVER_NAME_REGEX = /^[a-z0-9][a-z0-9-]{1,38}$/;
export const MCP_TOOL_NAME_REGEX = /^[A-Za-z0-9_-]{1,64}$/;

export const mcpPinnedToolSchema = z
  .object({
    name: z.string().regex(MCP_TOOL_NAME_REGEX),
    description: z.string(),
    inputSchemaSha256: z.string().regex(/^[0-9a-f]{64}$/),
  })
  .strict();
export const mcpPinnedToolsSchema = z.array(mcpPinnedToolSchema);
export type McpPinnedTool = z.infer<typeof mcpPinnedToolSchema>;

export const mcpDriftSchema = z
  .object({
    detectedAt: z.string(),
    added: z.array(z.string()),
    removed: z.array(z.string()),
    changed: z.array(z.object({ name: z.string(), field: z.enum(['description', 'inputSchema']) }).strict()),
  })
  .strict();
export type McpDrift = z.infer<typeof mcpDriftSchema>;

export function readPinnedTools(value: unknown): McpPinnedTool[] {
  return mcpPinnedToolsSchema.parse(value);
}

export function readDrift(value: unknown): McpDrift | null {
  return value === null ? null : mcpDriftSchema.parse(value);
}

export const mcpServerConnectionCreateSchema = z
  .object({
    name: z.string().regex(
      MCP_SERVER_NAME_REGEX,
      'Name must be a slug: lowercase letters, digits, and hyphens, starting with a letter or digit, 2-39 characters'
    ),
    url: z.string().url(),
    authHeaderName: z.string().trim().min(1).max(200).optional(),
    authHeaderValue: z.string().min(1).optional(),
    enabled: z.boolean().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.authHeaderValue !== undefined && value.authHeaderName === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['authHeaderName'],
        message: 'authHeaderName is required when authHeaderValue is provided',
      });
    }
  });

export const mcpServerConnectionUpdateSchema = z
  .object({
    name: z.string().regex(
      MCP_SERVER_NAME_REGEX,
      'Name must be a slug: lowercase letters, digits, and hyphens, starting with a letter or digit, 2-39 characters'
    ).optional(),
    url: z.string().url().optional(),
    enabled: z.boolean().optional(),
    authHeaderName: z.string().trim().min(1).max(200).nullable().optional(),
    authHeaderValue: z.string().min(1).nullable().optional(),
  })
  .strict();

export type McpServerConnectionCreateInput = z.infer<typeof mcpServerConnectionCreateSchema>;
export type McpServerConnectionUpdateInput = z.infer<typeof mcpServerConnectionUpdateSchema>;

export interface McpServerConnectionSafe {
  id: number;
  hubId: number;
  name: string;
  url: string;
  authHeaderName: string | null;
  hasAuthValue: boolean;
  enabled: boolean;
  createdByMemberId: number | null;
  createdAt: string;
  updatedAt: string;
  pinnedTools: McpPinnedTool[];
  pinnedAt: string | null;
  pinnedByMemberId: number | null;
  drift: McpDrift | null;
}

export interface McpServerConnectionListResult {
  connections: McpServerConnectionSafe[];
}

export interface McpServerConnectionProbeResult {
  ok: true;
  toolCount: number;
  toolNames: string[];
}
